/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Button, Group, Text, ThemeIcon } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconArrowLeft, IconBallpen } from '@tabler/icons';
import { FC, ReactNode, useContext, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate, useParams } from 'react-router-dom';
import PDFViewer from '../../Atoms/PDFViewer';
import { useModalState } from '../Dialogs';
import EditFinishedDocumentWarning from '../Event/Documents/EditFinishedDocumentWarning';
import PanePaper from '../PanePaper';
import { TestS } from '../../TestS';
import { ToastS } from '../../Service/ToastS';
import { ShareS } from '../../Service/ShareS';
import DocumentPreviewActions from './DocumentPreviewActions';
import { WindowContext } from '../../Service/Context/WindowContext';

const DocumentTemplatePreview: FC<{
  url: string;
  rightSection?: ReactNode;
  draft?: boolean;
  entityType: string;
  entity: any;
  patchEntity?: (patch: any, onSuccess?: () => void) => void;
  isPrintable?: boolean;
  portalSupported?: boolean;
}> = ({ url, rightSection, draft, entityType, entity, portalSupported, patchEntity, isPrintable }) => {
  const { id: eventId } = useParams();
  const navigate: NavigateFunction = useNavigate();
  const { pathname } = useLocation();
  const { show, toggle } = useModalState(false);
  const { ref, width } = useElementSize();
  const [blob, setBlob] = useState<Blob>();
  const [lastModified, setLastModified] = useState<Date>();
  const { sm } = useContext(WindowContext);
  const { id: entityId, locked, hasPayments, acceptedAt, hasReminders } = entity;
  const showDropdownVariant = sm;

  const editDocument = async () => {
    if (hasReminders) {
      ToastS.warn(
        'document-has-reminders',
        'Das Dokument kann nicht bearbeitet werden, da es mindestens eine Mahnung enthält.',
      );
      return;
    }
    if (draft || entityType === 'questionnaires') {
      navigate(pathname.replace('/preview', ''));
    } else if (locked) {
      ToastS.warn(
        'document-locked',
        'Das Dokument darf nicht mehr bearbeitet werden, da es bereits festgeschrieben wurde.',
      );
    } else if (hasPayments) {
      ToastS.warn(
        'document-has-payments',
        'Das Dokument darf nicht bearbeitet werden. Löschen Sie zunächst alle Zahlungen.',
      );
    } else {
      if (entityType === 'documents') {
        if (acceptedAt) {
          ToastS.warn(
            'document-accepted',
            'Das Angebot wurde bereits bestätigt. Lösche die Angebots-Bestätigung, um das Angebot zu bearbeiten.',
          );
          return;
        }
        const isAttached = await ShareS.isDocumentAttachedToSignedContract(entityId);
        if (isAttached) {
          ToastS.warn(
            'attached-to-signed-contract',
            'Das Angebot gehört zu einem unterzeichneten Vertrag. Lösche die Unterschrift vom Vertrag, um das Angebot zu bearbeiten.',
          );
          return;
        }
      }
      if (entityType === 'contracts' && !draft) {
        const isSigned = await ShareS.isContractSigned(entityId);
        if (isSigned) {
          ToastS.warn('contract-signed', 'Um den Vertrag zu bearbeiten, lösche zunächst die Kundenunterschrift.');
          return;
        }
      }
      toggle();
    }
  };

  return (
    <div ref={ref} style={{ display: 'flex' }}>
      <PanePaper withShadow withoutBorder rightSection={rightSection} borderlessVersion={sm}>
        {!sm && (
          <Group position="right" sx={{ position: 'relative' }}>
            {!draft && (
              <div style={{ display: 'flex', position: 'absolute', left: 0, alignItems: 'center', gap: 10 }}>
                <ActionIcon onClick={() => navigate(eventId ? `/events/${eventId}/documents` : '/documents')}>
                  <IconArrowLeft />
                </ActionIcon>
                <Text size="lg" weight="bolder">
                  Vorschau
                </Text>
              </div>
            )}
            <div>
              {sm ? (
                <ThemeIcon onClick={editDocument} data-testid={TestS.Navigate.Documents.TO_EDIT}>
                  <IconBallpen size={20} />
                </ThemeIcon>
              ) : (
                <Button
                  leftIcon={<IconBallpen size={20} />}
                  onClick={editDocument}
                  data-testid={TestS.Navigate.Documents.TO_EDIT}
                >
                  Bearbeiten
                </Button>
              )}
            </div>
            {showDropdownVariant && (
              <DocumentPreviewActions
                dropDownVesion
                portalSupported={portalSupported}
                entityType={entityType}
                entity={entity}
                documentFile={blob}
                setLastModified={setLastModified}
                patchEntity={patchEntity}
                isPrintable={isPrintable}
                editDocument={editDocument}
              />
            )}
          </Group>
        )}
        {sm && (
          <DocumentPreviewActions
            dropDownVesion
            portalSupported={portalSupported}
            entityType={entityType}
            entity={entity}
            documentFile={blob}
            setLastModified={setLastModified}
            patchEntity={patchEntity}
            isPrintable={isPrintable}
            editDocument={editDocument}
          />
        )}
        <PDFViewer
          setFile={setBlob}
          url={url}
          containerWidth={width - (showDropdownVariant ? 0 : 330)}
          lastModified={lastModified}
          isDraft={draft}
        />
      </PanePaper>
      {!showDropdownVariant && (
        <DocumentPreviewActions
          portalSupported={portalSupported}
          entityType={entityType}
          entity={entity}
          documentFile={blob}
          setLastModified={setLastModified}
          patchEntity={patchEntity}
          editDocument={editDocument}
          isPrintable={isPrintable}
        />
      )}
      {!draft && (
        <EditFinishedDocumentWarning
          eventId={entity.eventId}
          documentType={entity.documentType}
          entityType={entityType}
          entityId={entity.id}
          show={show}
          toggle={toggle}
          patchEntity={patchEntity}
        />
      )}
    </div>
  );
};

export default DocumentTemplatePreview;
